import axios from '@axios'
import { clearAxiosCache } from '@/libs/axios'
import sLocalStorage from '@core/utils/secure-storage'
import { useAuthStore } from '@/pinia/auth'
import { useWorkspaceStore } from '@/pinia/workspace'
import { useSubscriptionStore } from '@/pinia/subscription'
import { useTokenBalanceStore } from '@/pinia/tokenBalance'
import { useOnboardingStore } from '@/pinia/onboarding'
import { useStorageQuotaStore } from '@/pinia/storageQuota'
import { useSettingStore } from '@/pinia/setting'

export default {
  namespaced: true,
  
  state: {
    isActive: false,
    currentTenant: null,
    availableTenants: [],
    auditLogs: [],
    viewMode: 'normal', // normal, debug, readonly
    sessionStartTime: null,
    sessionDuration: 5 * 60 * 60 * 1000, // 5 hours in ms
    currentTime: Date.now(), // Reactive time for countdown
    requires2FASetup: false,
    requires2FA: false,
    twoFactorSetupData: null
  },
  
  getters: {
    isSuperadminMode: state => state.isActive,
    currentTenantName: state => state.currentTenant?.name || 'All Tenants',
    currentTenantId: state => state.currentTenant?.id || 0,
    sessionTimeRemaining: state => {
      if (!state.sessionStartTime) return 0
      const elapsed = state.currentTime - new Date(state.sessionStartTime).getTime()
      return Math.max(0, state.sessionDuration - elapsed)
    },
    formattedSessionTime: (state, getters) => {
      const remaining = getters.sessionTimeRemaining
      const hours = Math.floor(remaining / (60 * 60 * 1000))
      const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000))
      const seconds = Math.floor((remaining % (60 * 1000)) / 1000)
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
  },
  
  mutations: {
    SET_SUPERADMIN_MODE(state, payload) {
      state.isActive = payload.active
      state.sessionStartTime = payload.sessionStartTime || new Date().toISOString()
      state.availableTenants = payload.availableTenants || []
      state.requires2FASetup = payload.requires2FASetup || false
      state.requires2FA = payload.requires2FA || false
      
      // Persist to localStorage for axios interceptor
      this.commit('superadmin/PERSIST_SESSION')
    },
    
    SET_CURRENT_TENANT(state, tenant) {
      state.currentTenant = tenant
      this.commit('superadmin/PERSIST_SESSION')
    },
    
    SET_AVAILABLE_TENANTS(state, tenants) {
      state.availableTenants = tenants
    },
    
    SET_AUDIT_LOGS(state, logs) {
      state.auditLogs = logs
    },
    
    SET_VIEW_MODE(state, mode) {
      state.viewMode = mode
    },
    
    SET_2FA_SETUP_DATA(state, data) {
      state.twoFactorSetupData = data
    },

    UPDATE_CURRENT_TIME(state) {
      state.currentTime = Date.now()
    },
    
    CLEAR_SUPERADMIN_SESSION(state) {
      state.isActive = false
      state.currentTenant = null
      state.sessionStartTime = null
      state.requires2FASetup = false
      state.requires2FA = false
      state.twoFactorSetupData = null
      
      // Remove from localStorage
      localStorage.removeItem('superadmin_session')
    },
    
    PERSIST_SESSION(state) {
      const sessionData = {
        isActive: state.isActive,
        currentTenantId: state.currentTenant?.id || 0,
        currentTenantName: state.currentTenant?.name || 'All Tenants',
        sessionStartTime: state.sessionStartTime
      }
      localStorage.setItem('superadmin_session', JSON.stringify(sessionData))
    }
  },
  
  actions: {
    async activateSuperadminMode({ commit, dispatch }, loginResponse) {
      try {
        commit('SET_SUPERADMIN_MODE', {
          active: true,
          sessionStartTime: loginResponse.session_expires_at,
          availableTenants: loginResponse.available_tenants || [],
          requires2FASetup: loginResponse.requires_2fa_setup || false,
          requires2FA: loginResponse.requires_2fa || false
        })
        
        // Set axios headers for superadmin
        axios.defaults.headers.common['X-Superadmin-Mode'] = 'true'
        
        // Fetch available tenants if not provided in login response
        if (!loginResponse.available_tenants || loginResponse.available_tenants.length === 0) {
          await dispatch('fetchAvailableTenants')
        }
        
        // Start session timer
        dispatch('startSessionTimer')
        
        return loginResponse
      } catch (error) {
        console.error('Failed to activate superadmin mode:', error)
        throw error
      }
    },
    
    async fetchAvailableTenants({ commit }) {
      try {
        const response = await axios.get('/v1/superadmin/tenants', {
          params: { per_page: 100 }
        })

        // Response structure: { success, data: { tenants: { data: [...] }, summary } }
        const result = response.data.data || {}
        const tenants = result.tenants?.data || result.tenants || []

        commit('SET_AVAILABLE_TENANTS', Array.isArray(tenants) ? tenants : [])
        return response.data
      } catch (error) {
        console.error('Failed to fetch tenants:', error.response?.data || error.message)
        commit('SET_AVAILABLE_TENANTS', [])
        throw error
      }
    },
    
    async setup2FA({ commit }, tempToken = null) {
      try {
        const config = {}
        
        // If temporary token provided, use it for authorization
        if (tempToken) {
          config.headers = {
            'Authorization': `Bearer ${tempToken}`
          }
        }
        
        const response = await axios.post('/v1/2fa/setup', {}, config)
        commit('SET_2FA_SETUP_DATA', response.data)
        return response.data
      } catch (error) {
        console.error('2FA setup failed:', error)
        throw error
      }
    },
    
    async verify2FA({ commit }, { code, tempToken = null }) {
      try {
        const config = {}
        
        // If temporary token provided, use it for authorization
        if (tempToken) {
          config.headers = {
            'Authorization': `Bearer ${tempToken}`
          }
        }
        
        const response = await axios.post('/v1/2fa/verify', { code }, config)
        
        // If login was completed during verification (temporary setup flow)
        if (response.data.login_completed) {
          commit('SET_SUPERADMIN_MODE', { 
            active: true, 
            requires2FASetup: false,
            sessionStartTime: response.data.session_expires_at || new Date().toISOString(),
            availableTenants: response.data.available_tenants || []
          })
        } else {
          commit('SET_SUPERADMIN_MODE', { 
            active: true, 
            requires2FASetup: false,
            sessionStartTime: new Date().toISOString()
          })
        }
        
        return response.data
      } catch (error) {
        console.error('2FA verification failed:', error)
        throw error
      }
    },
    
    async switchTenant({ commit, dispatch, state }, tenantId) {
      // Capture pre-switch state so we can roll back if refresh fails.
      const previousTenant = state.currentTenant
      const previousHeader = axios.defaults.headers.common['X-Tenant-Override']

      try {
        console.log('Switching to tenant:', tenantId)

        let tenant = null

        if (tenantId === null || tenantId === 0) {
          // Switch to "all tenants" mode
          tenant = { id: 0, name: 'All Tenants', code: 'ALL' }
          delete axios.defaults.headers.common['X-Tenant-Override']
        } else {
          tenant = state.availableTenants.find(t => t.id === tenantId)

          if (!tenant) {
            throw new Error('Tenant not found in available tenants')
          }

          axios.defaults.headers.common['X-Tenant-Override'] = tenantId
        }

        // Commit state first so the axios interceptor reads the new tenant id
        // from localStorage (PERSIST_SESSION) on the very next request.
        commit('SET_CURRENT_TENANT', tenant)

        // Server-side audit log of the switch.
        const response = await axios.post('/v1/superadmin/switch-tenant', { tenant_id: tenantId })
        console.log('Switch tenant API response:', response.data)

        // Reload identity, permissions, entity, and clear stale caches.
        await dispatch('refreshTenantContext')

        return tenant
      } catch (error) {
        console.error('Tenant switch failed:', error.response?.data || error.message)

        // Roll back to the pre-switch state so we don't end up half-switched
        // (header pointing at tenant X, store data still tenant Y).
        commit('SET_CURRENT_TENANT', previousTenant)
        if (previousHeader === undefined) {
          delete axios.defaults.headers.common['X-Tenant-Override']
        } else {
          axios.defaults.headers.common['X-Tenant-Override'] = previousHeader
        }

        throw error
      }
    },

    async refreshTenantContext() {
      const authStore = useAuthStore()
      const response = await axios.get('/v1/user-data')
      const { userData } = response.data

      // Map abilities → Pinia permissions (same shape as App.vue beforeCreate).
      const abilities = userData.ability || []
      const permissions = abilities.map(({ action: subject }) => ({ action: 'manage', subject }))
      permissions.push({ action: 'manage', subject: 'view dashboard' })
      authStore.setPermissions(permissions)

      // Always keep 'superadmin' in the roles list — feature gates,
      // subscription lockdown, and onboarding lock all bypass on this role.
      const incomingRoles = Array.isArray(userData.roles) ? userData.roles : []
      const mergedRoles = incomingRoles.includes('superadmin')
        ? incomingRoles
        : ['superadmin', ...incomingRoles]

      const mergedUserData = userData.entity
        ? {
            ...authStore.userData,
            ...userData,
            roles: mergedRoles,
            role: userData.role,
            entity: userData.entity,
            is_superadmin: true,
          }
        : {
            ...authStore.userData,
            entity: null,
            role: 'superadmin',
            roles: ['superadmin'],
            is_superadmin: true,
          }

      authStore.setUserData(mergedUserData)

      // canNavigate() reads userData from SecureLS directly (not Pinia), so
      // we must re-write it here or route guards keep using stale permissions
      // until the next hard refresh.
      try {
        sLocalStorage.setItem('userData', JSON.stringify(mergedUserData))
      } catch (e) {
        console.warn('Failed to persist userData to SecureLS:', e)
      }

      // Drop cached responses from the previous tenant's context.
      await clearAxiosCache()

      // Reset Pinia stores that hold tenant-scoped data. They will re-fetch
      // lazily as components mount in the new tenant context.
      try {
        useWorkspaceStore().$reset()
        useSubscriptionStore().$reset()
        useTokenBalanceStore().$reset()
        useOnboardingStore().$reset()
        useStorageQuotaStore().$reset()
        useSettingStore().$reset()
      } catch (e) {
        console.warn('Failed to reset tenant-scoped Pinia stores:', e)
      }
    },
    
    async fetchAuditLogs({ commit }) {
      try {
        const response = await axios.get('/v1/superadmin/audit-logs')
        commit('SET_AUDIT_LOGS', response.data.data || [])
        return response.data
      } catch (error) {
        console.error('Failed to fetch audit logs:', error)
        throw error
      }
    },
    
    async exitSuperadminMode({ commit, dispatch }) {
      try {
        // Stop session timer
        dispatch('stopSessionTimer')

        // Clear axios headers
        delete axios.defaults.headers.common['X-Tenant-Override']
        delete axios.defaults.headers.common['X-Superadmin-Mode']

        // Clear superadmin state
        commit('CLEAR_SUPERADMIN_SESSION')

        // Clear main auth session (full logout)
        const authStore = useAuthStore()
        authStore.signOut()

        // Redirect to login
        window.location.href = '/login'
      } catch (error) {
        console.error('Failed to exit superadmin mode:', error)
        // Force redirect even on error
        window.location.href = '/login'
      }
    },
    
    startSessionTimer({ commit, dispatch, getters }) {
      // Clear any existing timer
      if (this._sessionTimer) {
        clearInterval(this._sessionTimer)
      }

      // Update time and check session expiry every second
      this._sessionTimer = setInterval(() => {
        // Update reactive time to trigger getter recomputation
        commit('UPDATE_CURRENT_TIME')

        const remaining = getters.sessionTimeRemaining

        if (remaining <= 0) {
          clearInterval(this._sessionTimer)
          this._sessionTimer = null
          dispatch('exitSuperadminMode')
          alert('Sesi superadmin telah berakhir demi keamanan.')
        }
      }, 1000)
    },

    stopSessionTimer() {
      if (this._sessionTimer) {
        clearInterval(this._sessionTimer)
        this._sessionTimer = null
      }
    }
  }
}